import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ShieldCheck, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HERO_IMG_A = "https://images.unsplash.com/photo-1768511771143-73e862326f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjBjb3VwbGUlMjBzbWlsaW5nJTIwYmVhY2h8ZW58MHx8fHwxNzc4NzEwMjYxfDA&ixlib=rb-4.1.0&q=85";
const HERO_IMG_B = "https://images.unsplash.com/photo-1739932907333-e518dc4fb674?auto=format&fit=crop&w=1600&q=85";

const initialForm = {
  full_name: "",
  age: "",
  coverage_amount: "10000",
  phone: "",
  email: "",
  zip_code: "",
  tobacco: "no",
};

export default function Hero() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.age || !form.phone || !form.email || !form.zip_code) {
      toast.error("Please complete every field so we can prepare your free quote.");
      return;
    }
    const ageNum = parseInt(form.age, 10);
    if (Number.isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
      toast.error("Please enter a valid age between 18 and 100.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, { ...form, age: ageNum });
      toast.success("Your free quote request has been received! A licensed agent will reach out soon.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("We couldn't submit your request. Please try again or call 833-309-LIFE.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="top" className="relative pt-20 isolate overflow-hidden" data-testid="hero-section">
      {/* Background image + navy overlay */}
      <div className="absolute inset-0 z-0 grid grid-cols-1 sm:grid-cols-2">
        <img
          src={HERO_IMG_A}
          alt="Senior couple enjoying peace of mind"
          className="w-full h-full object-cover"
        />
        <img
          src={HERO_IMG_B}
          alt="Senior couple sharing a quiet moment outdoors"
          className="hidden sm:block w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: text */}
          <div className="lg:col-span-7 text-white fade-up">
            <img
              src="/logo-blue.png"
              alt="Legacy Security Life — Final Expense Life Insurance Solutions"
              data-testid="hero-logo"
              className="w-44 sm:w-56 lg:w-64 h-auto mx-auto lg:mx-0 lg:-ml-2 mb-6 drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
            />
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-[#E8D49A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059] soft-pulse" />
              Trusted Final Expense Specialists
            </p>
            <h1 className="font-serif mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Protect Your Family From <span className="text-[#E8C97A]">Tomorrow's</span> Final Expenses, Today.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed">
              Affordable burial &amp; funeral coverage from <span className="text-[#E8C97A] font-semibold">$5,000 to $25,000</span>.
              Guaranteed acceptance for ages 50–85. No medical exam. Locked-in rates that never increase.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4 text-sm sm:text-base">
              <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#C5A059]" /> No Medical Exam</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#C5A059]" /> Rates Locked For Life</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#C5A059]" /> Coverage in 24 Hours</div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#plans"
                data-testid="hero-view-plans-btn"
                className="inline-flex items-center justify-center h-12 px-7 rounded-md bg-white text-navy font-semibold hover:bg-[#F1F5F9] transition-colors"
              >
                View Coverage Plans
              </a>
              <a
                href="tel:8333095433"
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center h-12 px-7 rounded-md border border-[#C5A059] text-[#E8C97A] font-semibold hover:bg-[#C5A059] hover:text-navy transition-colors"
              >
                Call 833-309-LIFE
              </a>
            </div>
          </div>

          {/* Right: lead capture */}
          <div className="lg:col-span-5 fade-up-delay-2">
            <div
              id="quote-form"
              data-testid="quote-form-card"
              className="bg-white rounded-xl shadow-2xl p-7 sm:p-8 border border-[#C5A059]/30 scroll-mt-28"
            >
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="h-5 w-5 text-[#C5A059]" />
                <span className="text-xs uppercase tracking-[0.2em] text-gold-deep font-semibold">Free Instant Quote</span>
              </div>
              <h2 className="font-serif text-2xl sm:text-[28px] text-navy leading-tight">
                Get Your Personalized Quote
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Takes 60 seconds. No obligation, ever.
              </p>

              {submitted ? (
                <div data-testid="quote-success" className="mt-6 p-5 rounded-md bg-[#F1F5F9] border border-[#C5A059]/40">
                  <h3 className="font-serif text-xl text-navy">Thank you!</h3>
                  <p className="text-sm text-navy/80 mt-2">A licensed agent will reach out within one business day with your personalized options.</p>
                  <Button
                    className="mt-4 bg-navy text-white hover:bg-[#152E55] h-10"
                    onClick={() => setSubmitted(false)}
                    data-testid="quote-submit-another"
                  >
                    Submit another quote
                  </Button>
                </div>
              ) : (
                <form className="mt-5 space-y-4" onSubmit={handleSubmit} data-testid="quote-form">
                  <div>
                    <Label htmlFor="full_name" className="text-navy font-medium">Full Name</Label>
                    <Input
                      id="full_name"
                      data-testid="quote-input-name"
                      placeholder="Jane Doe"
                      value={form.full_name}
                      onChange={(e) => handleChange("full_name", e.target.value)}
                      className="h-12 text-base mt-1.5 border-[#cfd6e0]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="age" className="text-navy font-medium">Age</Label>
                      <Input
                        id="age"
                        data-testid="quote-input-age"
                        type="number"
                        min={18}
                        max={100}
                        placeholder="65"
                        value={form.age}
                        onChange={(e) => handleChange("age", e.target.value)}
                        className="h-12 text-base mt-1.5 border-[#cfd6e0]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code" className="text-navy font-medium">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        data-testid="quote-input-zip"
                        placeholder="33602"
                        value={form.zip_code}
                        onChange={(e) => handleChange("zip_code", e.target.value)}
                        className="h-12 text-base mt-1.5 border-[#cfd6e0]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-navy font-medium">Coverage</Label>
                      <Select
                        value={form.coverage_amount}
                        onValueChange={(v) => handleChange("coverage_amount", v)}
                      >
                        <SelectTrigger className="h-12 mt-1.5 border-[#cfd6e0]" data-testid="quote-select-coverage">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5000">$5,000</SelectItem>
                          <SelectItem value="10000">$10,000</SelectItem>
                          <SelectItem value="15000">$15,000</SelectItem>
                          <SelectItem value="20000">$20,000</SelectItem>
                          <SelectItem value="25000">$25,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-navy font-medium">Tobacco Use</Label>
                      <Select value={form.tobacco} onValueChange={(v) => handleChange("tobacco", v)}>
                        <SelectTrigger className="h-12 mt-1.5 border-[#cfd6e0]" data-testid="quote-select-tobacco">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">Non-tobacco</SelectItem>
                          <SelectItem value="yes">Tobacco user</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-navy font-medium">Phone</Label>
                    <Input
                      id="phone"
                      data-testid="quote-input-phone"
                      type="tel"
                      placeholder="(555) 555-5555"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="h-12 text-base mt-1.5 border-[#cfd6e0]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-navy font-medium">Email</Label>
                    <Input
                      id="email"
                      data-testid="quote-input-email"
                      type="email"
                      placeholder="jane@email.com"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="h-12 text-base mt-1.5 border-[#cfd6e0]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit-btn"
                    className="w-full h-12 bg-[#C5A059] hover:bg-[#B08A3E] text-[#0B203B] font-semibold text-base mt-2"
                  >
                    {loading ? "Sending..." : "Get My Free Quote"}
                  </Button>

                  <p className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                    <Lock className="h-3.5 w-3.5" />
                    Your information is private and never sold.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
